<template>

  <!-- Conditional Sidebar: only once we’re actually logged in and have picked a company -->
  <component
    v-if="hasSidebar"  
    :is="currentSidebar"
    :userId="userStore.user.UserId"
    :userName="userStore.user.UserName"
    :userSurname="userStore.user.UserSurname"
    :userRole="userStore.currentCompany.role"
    :companyId="userStore.currentCompany.CompanyId"
    :companyName="companyStore.company.CompanyName"
  />

  <!-- Phone: a slim, light bar under the hamburger, so content scrolls under a
       calm bar instead of under a lone floating button (shown ≤768px only). -->
  <div v-if="hasSidebar" class="mobile-topbar" aria-hidden="true"></div>

  <!-- Page content -->
  <div id="app" :class="hasSidebar ? 'content' : 'content content--full'">
    <RouterView :key="$route.params.id" />
  </div>

  <!-- Ask Allen — one docked companion on every app surface (spec §3.5). Hidden
       on login/select-company (same gate as the sidebar). -->
  <AllenDock
    v-if="hasSidebar"
    :mode="allenMode"
    :suggested-questions="allenChips"
    :canned-answers="allenAnswers"
  />

</template>

<script>
import { computed, onBeforeMount } from 'vue'
import { RouterView, useRoute } from 'vue-router'

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import NavSidebar from './components/NavSidebar.vue'
import StoralyticDemoNavSidebar from "./components/StoralyticDemoNavSidebar.vue"
import AllenDock from './components/AllenDock.vue'
import { DEMO_ALLEN_CHIPS, DEMO_ALLEN_ANSWERS } from '@/demo/allenDemoConfig'

export default {
  components: {
    NavSidebar,
    RouterView,
    StoralyticDemoNavSidebar,
    AllenDock
  },
  
  setup() {
    const route = useRoute()

    // Dynamically determine if it's the "/storalytic_demo_store" or demo detection zone path
    const isDemoStoreRoute = computed(() => !!route.meta.isDemo)

    // Only initialize userStore and companyStore when not in the demo store route
    const userStore = !isDemoStoreRoute.value ? useUserStore() : null
    const companyStore = !isDemoStoreRoute.value ? useCompanyStore() : null

    // Bootstrapping on app start (or page reload)(skip entirely on demo route)
    onBeforeMount(async () => {
      if (isDemoStoreRoute.value) return

      // bail out if we're on login/registration/select‑company
      if (['/login','/registration','/select-company'].includes(route.path)) {
        return
      }

      // bail out if no token
      if (!userStore.isUserLoggedIn) {
        return
      }

      // 1) restore & re‑fetch user details
      await userStore.fetchUserDetails()

      // 2) now that currentCompany is set, fetch its details
      const cid = userStore.currentCompany?.CompanyId
      if (cid) {
        await companyStore.fetchCompanyDetails(cid)
      }
    })

    // Dynamically determine which sidebar to show based on the current route
    const currentSidebar = computed(() => {
        return isDemoStoreRoute.value ? "StoralyticDemoNavSidebar" : "NavSidebar";
    })

    const noSidebarRoutes = [ "/login", "/registration", "/select-company" ]
    // only show sidebar once we're past login/select‐company and have a real CompanyId
    const hasSidebar = computed(() => {
        if (isDemoStoreRoute.value) return true  
        if (!userStore || !userStore.isUserLoggedIn) return false
        if (noSidebarRoutes.includes(route.path))    return false
        return !!userStore.currentCompany?.CompanyId
    })

    // Docked Ask Allen config — demo tree gets the scripted chips + canned
    // answers (no live backend); the real app gets live mode with generic
    // starter prompts (the panel calls the grounded coach backend).
    const LIVE_ALLEN_CHIPS = [
      'What should I focus on first?',
      'How is my store performing?',
      'Which zone has the most opportunity?',
    ]
    const allenMode = computed(() => (isDemoStoreRoute.value ? 'demo' : 'live'))
    const allenChips = computed(() => (isDemoStoreRoute.value ? DEMO_ALLEN_CHIPS : LIVE_ALLEN_CHIPS))
    const allenAnswers = computed(() => (isDemoStoreRoute.value ? DEMO_ALLEN_ANSWERS : []))

    // Conditionally return stores based on the route
    return {
        ...(userStore && { userStore }),
        ...(companyStore && { companyStore }),
        currentSidebar,
        hasSidebar,
        allenMode,
        allenChips,
        allenAnswers,
    };
  }
}
</script>

<style scoped>
/* Page content adjustments — offset matches the real sidebar width (300px)
   via the shared --sidebar-w token (was a hard-coded 280px, a 20px overlap). */
.content {
  margin-left: var(--sidebar-w, 300px);
  padding: var(--content-pad, 20px);
  width: calc(100% - var(--sidebar-w, 300px));
  min-width: 0;          /* allow inner content to shrink instead of overflowing */
  max-width: 100%;
  overflow-x: clip;      /* hard guard: content can never scroll the page sideways */
}

/* Room under the last content so it can always be scrolled clear of the floating
   "Ask Allen" button (shown on every page that has the sidebar). */
.content:not(.content--full) {
  padding-bottom: calc(var(--content-pad, 20px) + 72px);
}

.content--full {
  margin-left: 0 !important;
  width: 100% !important;
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
}

@media (max-width: 768px) {
  .content,
  .content--full {
    margin-left: 0;
    width: 100%;
  }
}

/* Phone top bar: sits under the hamburger (z 1100) and the sidebar/backdrop
   (1100/1095), above the page. 60px = the hamburger's 10px offset + 40px + 10px. */
.mobile-topbar { display: none; }
@media (max-width: 768px) {
  .mobile-topbar {
    display: block;
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 60px;
    z-index: 1090;
    background: rgba(255, 255, 255, 0.9);
    -webkit-backdrop-filter: saturate(160%) blur(8px);
    backdrop-filter: saturate(160%) blur(8px);
    border-bottom: 1px solid #eef2f6;
    pointer-events: none;
  }
}
</style>